import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave, faDownload } from '@fortawesome/free-solid-svg-icons';
import Layout from '../Layout';
import { budgetService } from '../../services/budgetService';
import { CURRENCY_OPTIONS } from '../../utils/constants';
import { useFlashMessage } from '../FlashMessage';
import BudgetSection from './BudgetSection';
import DeliveryBackModal from '../DeliveryBackModal';
import RecordLoadProblem, { describeLoadFailure } from '../ui/RecordLoadProblem';
import { shownName, UseAutomaticNameButton } from '../ui/HeaderNameEditor';
import { goToTradeOps, useTradeOpsDesk } from '../../utils/tradeOpsDesk';
import logo from '../../assets/images/IMT_logo.png';
import './BudgetCalculator.css';

const SECTION_ORDER = [
  'PRODUCT_SALES',
  'PRODUCT_PURCHASE',
  'FREIGHT',
  'COMMISSIONS',
  'PURCHASING_COSTS',
  'LAYTIME',
  'FINANCING',
  'OTHER_EXPENSES',
  'BELOW_GROSS_ADJUSTMENTS',
];

const BREAKDOWN_SECTIONS = [
  'FREIGHT',
  'COMMISSIONS',
  'PURCHASING_COSTS',
  'LAYTIME',
  'FINANCING',
  'OTHER_EXPENSES',
];

const DEFAULT_LINE_TYPES = {
  PRODUCT_SALES: 'OUTGOING_INVOICE',
  PRODUCT_PURCHASE: 'INCOMING_INVOICE',
};

let tempIdCounter = 0;
const nextTempId = () => `temp_${++tempIdCounter}`;

const SEED_TEMPLATE = [
  // [section, label, entryMode, isSubtotal, isMargin, netMarginEffect]
  ['PRODUCT_SALES',            'Sub Total',                              'CALCULATED',    true,  false, null],
  ['PRODUCT_PURCHASE',         'Sub Total',                              'CALCULATED',    true,  false, null],
  ['FREIGHT',                  'Freight',                                'PER_UNIT',      false, false, null],
  ['FREIGHT',                  'Insurances & liabilities',               'PER_UNIT',      false, false, null],
  ['FREIGHT',                  'Surveyor charges',                       'PER_UNIT',      false, false, null],
  ['FREIGHT',                  'Brokerage',                              'PER_UNIT',      false, false, null],
  ['FREIGHT',                  'LTC Owner Loadport paid by IMT',         'PER_UNIT',      false, false, null],
  ['FREIGHT',                  'LTC Owner Disport paid by IMT',          'PER_UNIT',      false, false, null],
  ['COMMISSIONS',              'Paid by IMT',                            'PER_UNIT',      false, false, null],
  ['COMMISSIONS',              'Commission Mercurius',                   'PER_UNIT',      false, false, null],
  ['COMMISSIONS',              'Earned by IMT',                          'PER_UNIT',      false, false, 'ADD_BACK'],
  ['PURCHASING_COSTS',         'Paid by IMT',                            'PER_UNIT',      false, false, null],
  ['LAYTIME',                  'LTC Sale Side paid by IMT',              'PER_UNIT',      false, false, null],
  ['LAYTIME',                  'LTC Sale Side claimed by IMT',           'PER_UNIT',      false, false, null],
  ['LAYTIME',                  'LTC Purchase Side paid by IMT',          'PER_UNIT',      false, false, null],
  ['LAYTIME',                  'LTC Purchase Side claimed by IMT',       'PER_UNIT',      false, false, null],
  ['FINANCING',                'Financing Costs Purchase',               'BUDGET_AMOUNT', false, false, null],
  ['FINANCING',                'Financing Costs Sales',                  'BUDGET_AMOUNT', false, false, null],
  ['FINANCING',                'Bank Charges',                           'BUDGET_AMOUNT', false, false, null],
  ['OTHER_EXPENSES',           'Other expected expenses',                'PER_UNIT',      false, false, null],
  ['OTHER_EXPENSES',           'Budget Gross Margin',                    'CALCULATED',    false, true,  null],
  ['BELOW_GROSS_ADJUSTMENTS',  'Interest for company cash utilization',  'BUDGET_AMOUNT', false, false, 'DEDUCT'],
  ['BELOW_GROSS_ADJUSTMENTS',  'Deal related travel cost',               'BUDGET_AMOUNT', false, false, 'DEDUCT'],
  ['BELOW_GROSS_ADJUSTMENTS',  'Commission Mercurius (add back)',        'BUDGET_AMOUNT', false, false, 'ADD_BACK'],
  ['BELOW_GROSS_ADJUSTMENTS',  'Budget Net Margin',                      'CALCULATED',    false, true,  null],
];

const buildSeedLines = () =>
  SEED_TEMPLATE.map(([section, label, entryMode, isSubtotal, isMargin, nme], i) => ({
    id: nextTempId(),
    section,
    line_label: label,
    sequence: (i + 1) * 10,
    entry_mode: entryMode,
    is_system_line: true,
    is_subtotal: isSubtotal,
    is_margin_row: isMargin,
    net_margin_effect: nme,
    partner_name: '',
    partner_crm_id: null,
    description: '',
    amount_per_unit: null,
    budget_amount: null,
    percent_of_revenue: null,
    line_type: null,
  }));

const MarginSummaryStrip = ({ grossMarginLine, netMarginLine, currency }) => {
  const fmt = (val) => {
    if (val == null || val === '') return '0.00';
    return parseFloat(val).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const renderTile = (line, label) => {
    if (!line) return null;
    const num = parseFloat(line.budget_amount) || 0;
    const sign = num > 0 ? 'profit' : num < 0 ? 'loss' : 'zero';

    return (
      <div className={`margin-summary-tile tile-${sign}`}>
        <div className="margin-summary-info">
          <span className="margin-summary-label">{label}</span>
          <span className={`margin-summary-amount amount-${sign}`}>
            {currency} {fmt(line.budget_amount)}
          </span>
        </div>
        {sign !== 'zero' && (
          <span className={`margin-summary-badge sbadge-${sign}`}>
            {sign === 'profit' ? 'Profit' : 'Loss'}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="budget-margin-summary">
      {renderTile(grossMarginLine, 'Gross Margin')}
      {renderTile(netMarginLine, 'Net Margin')}
    </div>
  );
};

const BudgetCalculator = () => {
  const { budgetId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { showSuccess, showError } = useFlashMessage();

  // Row 127: the desk is the loaded budget's own, else the Trade Ops table's
  // hint, else (Back only) the desk this person can open. The hint alone made
  // a fuels budget opened by link -- or reloaded after its first save, which
  // drops the hint -- save back as Cargo.
  const [budgetCommodity, setBudgetCommodity] = useState(null);
  const tradeOpsDesk = useTradeOpsDesk({
    recordCommodity: budgetCommodity,
    hintCommodity: location.state?.commodityType,
  });
  const commodityType = tradeOpsDesk.commodity;

  const isNew = !budgetId || budgetId === 'new';

  const [header, setHeader] = useState({
    name: '',
    quantity: '',
    unit: null,
    currency_code: 'USD',
    status: 'DRAFT',
    offer_group_identifier: location.state?.offerGroupIdentifier || '',
    created_by: '',
  });
  // ``header.name`` is only the TYPED name ('' = automatic). A budget linked
  // to a deal number takes that deal's name, worked out by the server
  // (offer/deal_names.py) and refreshed from every answer.
  const [automaticBudgetName, setAutomaticBudgetName] = useState('');
  const [lines, setLines] = useState(() => (isNew ? buildSeedLines() : []));
  const [units, setUnits] = useState([]);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [headerId, setHeaderId] = useState(isNew ? null : parseInt(budgetId, 10));
  const [showBackModal, setShowBackModal] = useState(false);
  // Row 125: why an existing budget didn't load. While set, the screen shows
  // RecordLoadProblem INSTEAD of the calculator. A failed load used to clear
  // "Loading budget..." and draw the empty calculator, whose Save overwrote
  // the budget (or, for an address that isn't a number, created a new one).
  const [budgetLoadProblem, setBudgetLoadProblem] = useState(null);
  const [budgetLoadAttempt, setBudgetLoadAttempt] = useState(0);
  // Which budget in the address has actually loaded; saving any other is refused.
  const [loadedBudgetId, setLoadedBudgetId] = useState(null);

  // ---- Load units -----------------------------------------------------------
  useEffect(() => {
    budgetService.getUnits()
      .then((data) => setUnits(data || []))
      .catch((err) => console.error('Failed to load units:', err));
  }, []);

  // ---- Load existing budget ------------------------------------------------
  useEffect(() => {
    setBudgetLoadProblem(null);
    if (!isNew && budgetId) {
      loadBudget(budgetId);
    }
  }, [budgetId, budgetLoadAttempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBudget = async (id) => {
    setIsLoading(true);
    try {
      const data = await budgetService.getHeader(id);
      setHeader({
        name: data.name || '',
        quantity: data.quantity || '',
        unit: data.unit || null,
        currency_code: data.currency_code || 'USD',
        status: data.status || 'DRAFT',
        offer_group_identifier: data.offer_group_identifier || '',
        created_by: data.created_by || '',
      });
      setAutomaticBudgetName(data.automatic_name);
      setHeaderId(data.id);
      setLines(data.lines || []);
      setBudgetCommodity(data.commodity_type || null);
      setLoadedBudgetId(id);
    } catch (err) {
      console.error(err);
      // Keep the answer: a refused or missing budget shows the message,
      // never the empty calculator.
      setBudgetLoadProblem(describeLoadFailure(err));
    } finally {
      setIsLoading(false);
    }
  };

  // ---- Client-side recalculation -------------------------------------------
  const recalcLocally = useCallback(
    (currentLines, qty) => {
      const q = parseFloat(qty) || 0;
      let updated = currentLines.map((l) => ({ ...l }));

      const NEGATE_TYPES = new Set(['CREDIT_NOTE', 'DEBIT_NOTE']);

      // Pass 1: per-line amounts
      updated.forEach((l) => {
        if (l.is_subtotal || l.is_margin_row) return;
        const sign = NEGATE_TYPES.has(l.line_type) ? -1 : 1;
        if (l.entry_mode === 'PER_UNIT') {
          const apu = parseFloat(l.amount_per_unit) || 0;
          l.budget_amount = (apu * sign * q).toFixed(2);
        } else if (l.entry_mode === 'BUDGET_AMOUNT') {
          const ba = parseFloat(l.budget_amount) || 0;
          l.amount_per_unit = q !== 0 ? (ba / q).toFixed(4) : '0';
        }
      });

      // Pass 2: section sums (ADD_BACK lines are income, so negate them)
      const sums = {};
      updated.forEach((l) => {
        if (l.is_subtotal || l.is_margin_row) return;
        const amt = parseFloat(l.budget_amount) || 0;
        const sign = l.net_margin_effect === 'ADD_BACK' ? -1 : 1;
        sums[l.section] = (sums[l.section] || 0) + (amt * sign);
      });
      const outgoing = sums['PRODUCT_SALES'] || 0;
      const incoming = sums['PRODUCT_PURCHASE'] || 0;

      // Write subtotals
      updated.forEach((l) => {
        if (!l.is_subtotal) return;
        const s = sums[l.section] || 0;
        l.budget_amount = s.toFixed(2);
        l.amount_per_unit = q !== 0 ? (s / q).toFixed(4) : '0';
      });

      // Pass 3: breakdown total
      const breakdownTotal = BREAKDOWN_SECTIONS.reduce(
        (acc, s) => acc + (sums[s] || 0),
        0
      );

      // Pass 4: gross margin
      const gross = outgoing - incoming - breakdownTotal;

      // Pass 5: net margin (uses net_margin_effect field, not label parsing)
      let net = gross;
      updated.forEach((l) => {
        if (l.section !== 'BELOW_GROSS_ADJUSTMENTS' || l.is_margin_row) return;
        const ba = parseFloat(l.budget_amount) || 0;
        if (l.net_margin_effect === 'ADD_BACK') {
          net += ba;
        } else {
          net -= ba;
        }
      });

      // Pass 6: write margin rows
      updated.forEach((l) => {
        if (!l.is_margin_row) return;
        const lbl = (l.line_label || '').toLowerCase();
        const val = lbl.includes('gross') ? gross : net;
        l.budget_amount = val.toFixed(2);
        l.amount_per_unit = q !== 0 ? (val / q).toFixed(4) : '0';
      });

      // Pass 7: percent of revenue (breakdown + margin rows only, not sales/purchase)
      const noPercentSections = new Set(['PRODUCT_SALES', 'PRODUCT_PURCHASE']);
      updated.forEach((l) => {
        if (noPercentSections.has(l.section)) {
          l.percent_of_revenue = null;
          return;
        }
        const ba = parseFloat(l.budget_amount) || 0;
        l.percent_of_revenue = outgoing !== 0 ? ((ba / outgoing) * 100).toFixed(4) : '0';
      });

      return updated;
    },
    []
  );

  // Recalc whenever lines or quantity change
  const triggerRecalc = useCallback(
    (newLines, qty) => {
      const recalced = recalcLocally(newLines, qty);
      setLines(recalced);
    },
    [recalcLocally]
  );

  // ---- Handlers ------------------------------------------------------------
  const handleHeaderChange = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);

    if (field === 'quantity') {
      triggerRecalc(lines, value);
    }
  };

  const handleLineChange = (lineId, updates) => {
    setHasUnsavedChanges(true);
    setLines((prev) => {
      const next = prev.map((l) => (l.id === lineId ? { ...l, ...updates } : l));
      return recalcLocally(next, header.quantity);
    });
  };

  const handleAddRow = (sectionCode, entryMode) => {
    const sectionLines = lines.filter((l) => l.section === sectionCode && !l.is_subtotal && !l.is_margin_row);
    const maxSeq = sectionLines.reduce((m, l) => Math.max(m, l.sequence || 0), 0);

    const newLine = {
      id: nextTempId(),
      section: sectionCode,
      line_label: '',
      sequence: maxSeq + 5,
      partner_name: '',
      partner_crm_id: null,
      description: '',
      amount_per_unit: null,
      budget_amount: null,
      percent_of_revenue: null,
      entry_mode: entryMode,
      line_type: DEFAULT_LINE_TYPES[sectionCode] || null,
      is_system_line: false,
      is_subtotal: false,
      is_margin_row: false,
      net_margin_effect: sectionCode === 'BELOW_GROSS_ADJUSTMENTS' ? 'DEDUCT' : null,
    };

    setHasUnsavedChanges(true);
    setLines((prev) => {
      const updated = [...prev, newLine];
      return recalcLocally(updated, header.quantity);
    });
  };

  const handleRemoveRow = (lineId) => {
    setHasUnsavedChanges(true);
    setLines((prev) => {
      const updated = prev.filter((l) => l.id !== lineId);
      return recalcLocally(updated, header.quantity);
    });
  };

  // ---- Pull from linked enquiry --------------------------------------------
  // Fills 3rd-party cost, sales and purchase rows from the enquiry(ies) linked
  // to this budget by its D-number, once recaps exist. Fill-blank-only: the
  // backend already drops rows that duplicate a filled line, so we simply
  // append the returned descriptors as new (removable) rows.
  const handlePullFromEnquiry = async () => {
    if (!headerId) {
      showError('Save the budget first, then pull from the linked deal.');
      return;
    }
    if (!(header.offer_group_identifier || '').trim()) {
      showError('Add a linked deal (D-number) first, then save.');
      return;
    }
    if (hasUnsavedChanges) {
      showError('Save your changes first, then pull from the linked deal.');
      return;
    }

    setIsPulling(true);
    try {
      const result = await budgetService.pullFromEnquiry(headerId);
      const rows = result.rows || [];

      if (rows.length === 0) {
        showError(result.detail || 'Nothing to pull from the linked deal yet.');
        return;
      }

      setLines((prev) => {
        const seqBySection = {};
        prev.forEach((l) => {
          if (l.is_subtotal || l.is_margin_row) return;
          seqBySection[l.section] = Math.max(seqBySection[l.section] || 0, l.sequence || 0);
        });

        const additions = rows.map((d) => {
          const nextSeq = (seqBySection[d.section] || 0) + 5;
          seqBySection[d.section] = nextSeq;
          return {
            id: nextTempId(),
            section: d.section,
            line_label: d.line_label || '',
            sequence: nextSeq,
            entry_mode: d.entry_mode || 'PER_UNIT',
            is_system_line: false,
            is_subtotal: false,
            is_margin_row: false,
            net_margin_effect: null,
            partner_name: d.partner_name || '',
            partner_crm_id: d.partner_crm_id ?? null,
            description: d.description || '',
            amount_per_unit: d.amount_per_unit ?? null,
            budget_amount: d.budget_amount ?? null,
            percent_of_revenue: null,
            line_type: d.line_type || null,
          };
        });

        return recalcLocally([...prev, ...additions], header.quantity);
      });
      setHasUnsavedChanges(true);

      const parts = [`Pulled ${rows.length} row${rows.length === 1 ? '' : 's'} from the linked enquiry.`];
      if (result.skipped) {
        parts.push(`${result.skipped} skipped (already filled in).`);
      }
      showSuccess(parts.join(' '));
      (result.warnings || []).forEach((w) => showError(w));
    } catch (err) {
      console.error('Pull from enquiry failed:', err);
      showError(err.response?.data?.detail || 'Failed to pull from the linked enquiry.');
    } finally {
      setIsPulling(false);
    }
  };

  // ---- Back navigation -----------------------------------------------------
  const handleBack = () => {
    if (hasUnsavedChanges) {
      setShowBackModal(true);
    } else {
      goToTradeOps(history, tradeOpsDesk, { activeTab: 'budgets' });
    }
  };

  // ---- Save ----------------------------------------------------------------
  const handleSave = async () => {
    // Row 125: a budget named in the address is only ever saved from its own
    // loaded record. Without this the empty calculator updated it with blanks,
    // or created a new budget when the address isn't a number.
    if (!isNew && loadedBudgetId !== budgetId) {
      showError("This budget hasn't loaded, so it can't be saved. Reload the page and try again.");
      return;
    }
    if (!header.quantity || parseFloat(header.quantity) <= 0) {
      showError('Quantity must be greater than zero');
      return;
    }

    setIsSaving(true);
    try {
      let savedId = headerId;

      const headerPayload = {
        name: header.name,
        quantity: header.quantity,
        unit: header.unit || null,
        currency_code: header.currency_code,
        status: 'DRAFT',
        commodity_type: commodityType,
        offer_group_identifier: (header.offer_group_identifier || '').trim() || null,
      };

      if (!savedId) {
        const created = await budgetService.createHeader(headerPayload);
        savedId = created.id;
        setHeaderId(savedId);

        await budgetService.seedLines(savedId);

        // Persist any user-added rows created before first save
        const userRows = lines
          .filter((l) => String(l.id).startsWith('temp_') && !l.is_system_line)
          .map((l) => ({
            section: l.section,
            line_label: l.line_label,
            sequence: l.sequence,
            partner_name: l.partner_name || '',
            partner_crm_id: l.partner_crm_id,
            description: l.description || '',
            amount_per_unit: l.amount_per_unit,
            budget_amount: l.budget_amount,
            entry_mode: l.entry_mode,
            line_type: l.line_type || null,
            is_system_line: false,
            is_subtotal: false,
            is_margin_row: false,
            net_margin_effect: l.net_margin_effect || null,
          }));

        if (userRows.length > 0) {
          const seeded = await budgetService.calculate(savedId);
          const seededLines = seeded.lines || [];
          await budgetService.updateHeader(savedId, {
            ...headerPayload,
            lines: [
              ...seededLines.map((l) => ({
                id: l.id,
                section: l.section,
                line_label: l.line_label,
                sequence: l.sequence,
                partner_name: l.partner_name || '',
                partner_crm_id: l.partner_crm_id,
                description: l.description || '',
                amount_per_unit: l.amount_per_unit,
                budget_amount: l.budget_amount,
                entry_mode: l.entry_mode,
                line_type: l.line_type || null,
                is_system_line: l.is_system_line,
                is_subtotal: l.is_subtotal,
                is_margin_row: l.is_margin_row,
                net_margin_effect: l.net_margin_effect || null,
              })),
              ...userRows,
            ],
          });
        }
      } else {
        const linePayload = lines
          .filter((l) => !String(l.id).startsWith('temp_'))
          .map((l) => ({
            id: l.id,
            section: l.section,
            line_label: l.line_label,
            sequence: l.sequence,
            partner_name: l.partner_name || '',
            partner_crm_id: l.partner_crm_id,
            description: l.description || '',
            amount_per_unit: l.amount_per_unit,
            budget_amount: l.budget_amount,
            entry_mode: l.entry_mode,
            line_type: l.line_type || null,
            is_system_line: l.is_system_line,
            is_subtotal: l.is_subtotal,
            is_margin_row: l.is_margin_row,
            net_margin_effect: l.net_margin_effect || null,
          }));

        const newRows = lines
          .filter((l) => String(l.id).startsWith('temp_'))
          .map((l) => ({
            section: l.section,
            line_label: l.line_label,
            sequence: l.sequence,
            partner_name: l.partner_name || '',
            partner_crm_id: l.partner_crm_id,
            description: l.description || '',
            amount_per_unit: l.amount_per_unit,
            budget_amount: l.budget_amount,
            entry_mode: l.entry_mode,
            line_type: l.line_type || null,
            is_system_line: false,
            is_subtotal: false,
            is_margin_row: false,
            net_margin_effect: l.net_margin_effect || null,
          }));

        await budgetService.updateHeader(savedId, {
          ...headerPayload,
          lines: [...linePayload, ...newRows],
        });
      }

      // Server recalculate for canonical values
      const recalculated = await budgetService.calculate(savedId);
      setHeader((prev) => ({
        ...prev,
        status: recalculated.status,
      }));
      setAutomaticBudgetName(recalculated.automatic_name);
      setLines(recalculated.lines || []);
      setHasUnsavedChanges(false);

      showSuccess('Budget saved successfully');

      // Navigate to edit URL if this was a new budget
      if (isNew) {
        history.replace(`/budget-calculator/${savedId}`);
      }
    } catch (err) {
      console.error('Save failed:', err);
      // A budget with no typed name and no deal to take one from is refused by
      // the server, which says why (offer/deal_names.py): show its words.
      const nameRefusal = err.response?.data?.name;
      showError(
        (Array.isArray(nameRefusal) ? nameRefusal.join(' ') : nameRefusal)
        || err.response?.data?.detail
        || 'Failed to save budget'
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Derived values for display ------------------------------------------
  // The typed name, else the deal's name (the server's display_name once saved).
  const budgetName = shownName(header.name, automaticBudgetName);

  const outgoingSubtotal = lines
    .filter((l) => l.section === 'PRODUCT_SALES' && l.is_subtotal)
    .reduce((s, l) => s + (parseFloat(l.budget_amount) || 0), 0);

  const linesBySection = {};
  SECTION_ORDER.forEach((sec) => {
    linesBySection[sec] = lines
      .filter((l) => l.section === sec)
      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  });

  const grossMarginLine = lines.find(
    (l) => l.is_margin_row && l.line_label?.toLowerCase().includes('gross')
  );
  const netMarginLine = lines.find(
    (l) => l.is_margin_row && l.line_label?.toLowerCase().includes('net')
  );

  // ---- Render --------------------------------------------------------------
  if (isLoading) {
    return (
      <Layout>
        <div className="budget-calculator-page">
          <div className="budget-loading">Loading budget...</div>
        </div>
      </Layout>
    );
  }

  if (budgetLoadProblem && !isNew) {
    return (
      <Layout>
        <div className="budget-calculator-page">
          <RecordLoadProblem
            problem={budgetLoadProblem}
            recordName="budget"
            areaName="budgets"
            backLabel="Back to Budgets"
            onBack={() => goToTradeOps(history, tradeOpsDesk, { activeTab: 'budgets' })}
            onRetry={() => setBudgetLoadAttempt((n) => n + 1)}
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="budget-calculator-page">
        {/* Unsaved changes back modal */}
        {showBackModal && (
          <DeliveryBackModal
            message="Are you sure you want to go back? Your budget changes will not be saved."
            onConfirm={() => goToTradeOps(history, tradeOpsDesk, { activeTab: 'budgets' })}
            onCancel={() => setShowBackModal(false)}
            draftLabel="Save"
            onDraft={async () => {
              setShowBackModal(false);
              await handleSave();
              goToTradeOps(history, tradeOpsDesk, { activeTab: 'budgets' });
            }}
          />
        )}

        {/* Header bar */}
        <div className="budget-header-bar">
          <div className="budget-header-left">
            <button
              className="budget-back-btn"
              onClick={handleBack}
              title="Back to Trade Operations"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <img src={logo} alt="IMT" className="budget-logo" />
            <div className="budget-title-group">
              <h1 className="budget-page-title">Budget Calculator</h1>
              {budgetName && (
                <span className="budget-page-subtitle">{budgetName}</span>
              )}
            </div>
            {header.status === 'SUBMITTED' && (
              <span className="budget-status-badge submitted">Submitted</span>
            )}
          </div>
          <div className="budget-header-actions">
            <button
              className="budget-save-btn"
              onClick={handleSave}
              disabled={isSaving}
            >
              <FontAwesomeIcon icon={faSave} />
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        <div className="budget-content">
          {/* Global fields header card */}
          <div className="budget-global-fields">
            <div className="budget-global-fields-header">
              <span className="budget-global-fields-title">Budget Details</span>
              {hasUnsavedChanges && (
                <span className="budget-unsaved-badge">
                  <i className="fas fa-circle"></i> Unsaved
                </span>
              )}
            </div>
            <div className="budget-global-fields-body">
              <div className="budget-field-group">
                <label>Budget Name</label>
                <input
                  type="text"
                  value={header.name}
                  onChange={(e) => handleHeaderChange('name', e.target.value)}
                  className="budget-input name-input"
                  placeholder={automaticBudgetName || 'e.g. Coal deal Australia Q2'}
                />
                <UseAutomaticNameButton
                  typedName={header.name}
                  automaticName={automaticBudgetName}
                  onUseAutomatic={() => handleHeaderChange('name', '')}
                  className="record-name-use-automatic-below"
                />
              </div>
              <div className="budget-field-group">
                <label>Quantity</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={header.quantity}
                  onChange={(e) => handleHeaderChange('quantity', e.target.value)}
                  className="budget-input quantity-input"
                  placeholder="Quantity will kick off calculations"
                />
              </div>
              <div className="budget-field-group">
                <label>Unit</label>
                <select
                  value={header.unit || ''}
                  onChange={(e) => handleHeaderChange('unit', e.target.value ? parseInt(e.target.value, 10) : null)}
                  className="budget-input currency-select"
                >
                  <option value="">-- Select Unit --</option>
                  {units.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="budget-field-group">
                <label>Currency</label>
                <select
                  value={header.currency_code}
                  onChange={(e) => handleHeaderChange('currency_code', e.target.value)}
                  className="budget-input currency-select"
                >
                  {CURRENCY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="budget-field-group">
                <label title="D-number from the deal recap. Linking here lets the delivery's Finance Calculator auto-pull this budget.">
                  Linked Deal (D-number)
                </label>
                <input
                  type="text"
                  value={header.offer_group_identifier || ''}
                  onChange={(e) => handleHeaderChange('offer_group_identifier', e.target.value)}
                  className="budget-input name-input"
                  placeholder="e.g. D1000001"
                />
                <button
                  type="button"
                  onClick={handlePullFromEnquiry}
                  disabled={isPulling}
                  title="Fill 3rd-party costs, sales and purchase rows from this deal's enquiry (once recaps exist)."
                  style={{
                    marginTop: 6,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 10px',
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#0277BE',
                    background: '#fff',
                    border: '1px solid #0277BE',
                    borderRadius: 4,
                    cursor: isPulling ? 'default' : 'pointer',
                    opacity: isPulling ? 0.6 : 1,
                  }}
                >
                  <FontAwesomeIcon icon={faDownload} />
                  {isPulling ? 'Pulling...' : 'Pull from enquiry'}
                </button>
              </div>
            </div>

            {/* Margin summary strip */}
            {(grossMarginLine || netMarginLine) && (
              <MarginSummaryStrip
                grossMarginLine={grossMarginLine}
                netMarginLine={netMarginLine}
                currency={header.currency_code}
              />
            )}
          </div>

          {/* Product Sales card */}
          <div className="budget-section-card">
            <div className="budget-section-card-header">
              <h3>Product Sales:</h3>
            </div>
            <div className="budget-section-sub-header">
              <span>Outgoing Invoices / Credit Notes:</span>
            </div>
            <div className="budget-grid-container">
              <table className="budget-grid">
                <thead>
                  <tr className="budget-column-header">
                    <th className="col-type">Type</th>
                    <th className="col-partner">Partner</th>
                    <th className="col-description">Description</th>
                    <th className="col-amount-per-unit">Amount per unit</th>
                    <th className="col-percent">%</th>
                    <th className="col-budget-amount">Budget amount</th>
                    <th className="col-actions"></th>
                  </tr>
                </thead>
                <tbody>
                  <BudgetSection
                    sectionCode="PRODUCT_SALES"
                    lines={linesBySection['PRODUCT_SALES'] || []}
                    quantity={header.quantity}
                    outgoingSubtotal={outgoingSubtotal}
                    onLineChange={handleLineChange}
                    onAddRow={handleAddRow}
                    onRemoveRow={handleRemoveRow}
                    hideHeader
                  />
                </tbody>
              </table>
            </div>
          </div>

          {/* Product Purchase card */}
          <div className="budget-section-card">
            <div className="budget-section-card-header">
              <h3>Product Purchase:</h3>
            </div>
            <div className="budget-section-sub-header">
              <span>Incoming Invoices / Debit Notes:</span>
            </div>
            <div className="budget-grid-container">
              <table className="budget-grid">
                <thead>
                  <tr className="budget-column-header">
                    <th className="col-type">Type</th>
                    <th className="col-partner">Partner</th>
                    <th className="col-description">Description</th>
                    <th className="col-amount-per-unit">Amount per unit</th>
                    <th className="col-percent">%</th>
                    <th className="col-budget-amount">Budget amount</th>
                    <th className="col-actions"></th>
                  </tr>
                </thead>
                <tbody>
                  <BudgetSection
                    sectionCode="PRODUCT_PURCHASE"
                    lines={linesBySection['PRODUCT_PURCHASE'] || []}
                    quantity={header.quantity}
                    outgoingSubtotal={outgoingSubtotal}
                    onLineChange={handleLineChange}
                    onAddRow={handleAddRow}
                    onRemoveRow={handleRemoveRow}
                    hideHeader
                  />
                </tbody>
              </table>
            </div>
          </div>

          {/* 3rd party costs parent card */}
          <div className="budget-breakdown-card">
            <div className="budget-breakdown-card-header">
              <h3>3rd party costs</h3>
            </div>
            <div className="budget-breakdown-children">
              {BREAKDOWN_SECTIONS.map((sec) => (
                <BudgetSection
                  key={sec}
                  sectionCode={sec}
                  lines={linesBySection[sec] || []}
                  quantity={header.quantity}
                  outgoingSubtotal={outgoingSubtotal}
                  onLineChange={handleLineChange}
                  onAddRow={handleAddRow}
                  onRemoveRow={handleRemoveRow}
                  isBreakdownChild
                />
              ))}
            </div>
          </div>

          {/* Below-Gross Adjustments card */}
          <div className="budget-section-card">
            <div className="budget-section-card-header">
              <h3>Below-Gross Adjustments</h3>
            </div>
            <div className="budget-grid-container">
              <table className="budget-grid">
                <thead>
                  <tr className="budget-column-header">
                    <th className="col-type">Type</th>
                    <th className="col-effect">Effect</th>
                    <th className="col-partner">Partner</th>
                    <th className="col-description">Description</th>
                    <th className="col-amount-per-unit">Amount per unit</th>
                    <th className="col-percent">%</th>
                    <th className="col-budget-amount">Budget amount</th>
                    <th className="col-actions"></th>
                  </tr>
                </thead>
                <tbody>
                  <BudgetSection
                    sectionCode="BELOW_GROSS_ADJUSTMENTS"
                    lines={linesBySection['BELOW_GROSS_ADJUSTMENTS'] || []}
                    quantity={header.quantity}
                    outgoingSubtotal={outgoingSubtotal}
                    onLineChange={handleLineChange}
                    onAddRow={handleAddRow}
                    onRemoveRow={handleRemoveRow}
                    hideHeader
                  />
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>
    </Layout>
  );
};

export default BudgetCalculator;
